import React, { useEffect, useMemo, useState } from 'react';
import { PlatoClientContext } from './PlatoClientContext';
import { keycloak, login, logout } from './authentication/authentication';
import { client as platoClient, common } from 'plato-js-client';
import { isTest } from './mixins/EnvironmentMixin';
import { AuthenticationContext } from './authentication/authentication-context';
import Loader from 'react-loader-spinner';
import NavBar from './NavBar';
import { Col, Row, Container } from 'reactstrap';

const isDevMode = isTest();
const apiRoot = sessionStorage.apiHost
    ? sessionStorage.apiHost
    : process.env.REACT_APP_PLATO_API_URL ?? '';

if ('' === apiRoot) {
    throw new Error('Configuration Error: Plato API url is missing or empty');
}

const platoApiHostSettings = {
    // Deal with missing trailing slash or v2 suffix, let plato-js-client do its thing.
    apiRoot: apiRoot.replace(/(\/v2\/?)$/, ''),
    apiPrefix: '/v2',
};

function PlatoClientProvider({ children }) {
    const [ user, setUser ] = useState(undefined);
    const [ error, setError ] = useState(undefined);
    const [ client, setClient ] = useState(undefined);
    const [ initialised, setInitialised ] = useState(false);

    const userId = user?.id;
    const isAuthenticated = !!userId;

    const clientContext = useMemo(() => ({
        client,
    }), [ initialised ]);

    const authenticationContext = useMemo(() => ({
        login,
        logout,
        user,
        userId,
        isAuthenticated,
    }), [ initialised, userId, isAuthenticated ]);

    useEffect(() => {
        if (client) {
            return;
        }

        if (initialised) {
            // Guard against keycloak/keycloak#12745 to prevent the init being called multiple times.
            return;
        }

        let cancelled = false;
        const resolveCurrentUserHelper = async (configuredPlatoClient) => {
            // Get all information about the owner of the token retrievable from the "whoami?" endpoint.
            const userInfo = await configuredPlatoClient.whoAmi();

            if (cancelled) {
                return;
            }

            // Get more detailed information that's specific to the concrete type of actor.
            const concreteActorType = common.ActorInstance.call(this, userInfo.entity.type.toLowerCase());
            const userInstance = new concreteActorType(userInfo.entity.id);
            userInstance.mutateResponse(userInfo.entity);

            if (cancelled) {
                return;
            }

            return userInstance;
        };

        const authenticateClientWithKeycloak = async () => {

            // NOTE: Defer state updates until the end of this async operation. Since this runs outside React's
            // synchronous flow, any state change may trigger an immediate re-render of dependent components.
            // This happens frequently whenever we await between multiple updates.

            if (cancelled) {
                return;
            }

            try {
                const authResult = await keycloak.init({
                    onLoad: 'check-sso',
                    silentCheckSsoRedirectUri: `${ window.location.origin }/silent-check-sso.html`,
                    pkceMethod: 'S256',
                    enableLogging: isDevMode,
                    // RES-3100: Keep this disabled until we upgrade to keycloak-js v26.
                    useNonce: false,
                    // Because our Keycloak instance lives on a different domain this has to be false or the
                    // browser third-party cookie policy will result in periodic full-page refreshes.
                    checkLoginIframe: false,
                });

                if (!authResult) {
                    setClient(platoClient.getInstance());
                    setInitialised(true);
                    return;
                }

                // Reconfigure the HTTP settings of the plato-js-client instance with the Keycloak token.
                const reconfiguredPlatoClient = platoClient
                    .getInstance()
                    .setInstance({
                        ...platoApiHostSettings,
                        keycloakInstance: keycloak,
                        useKeycloakMiddleware: true,
                    });

                const currentUser = await resolveCurrentUserHelper(reconfiguredPlatoClient);
                if (undefined === currentUser) {
                    setError("Cannot retrieve user information from the API");
                    setInitialised(false);
                    return;
                }

                setUser(currentUser);
                setClient(reconfiguredPlatoClient);
                setInitialised(true);
            } catch (e) {
                if (cancelled) {
                    throw e;
                }

                const message = e?.message ?? "Unexpected error during the authentication flow";
                setError(message);
                throw e;
            }
        };

        // NOTE: Though this doesn't strictly belong here, it keeps all keycloak-specific features
        // confined into this provider.
        keycloak.onTokenExpired = () => {
            keycloak.updateToken(-1)
                .catch(() => {
                    if (cancelled) {
                        return;
                    }

                    setError(`Your session has expired. If you're not signed-in automatically please refresh the page.`);
                });
        };

        keycloak.onAuthLogout = () => {
            if (cancelled) {
                return;
            }

            setUser(undefined);
        };

        authenticateClientWithKeycloak();

        return () => {
            cancelled = true;

            keycloak.onAuthLogout = undefined;
            keycloak.onTokenExpired = undefined;
        };
    }, []);

    if (error) {
        return (
            <div>Authentication Error: { error }</div>
        );
    }

    if (initialised) {
        return (
            <PlatoClientContext.Provider value={ clientContext }>
                <AuthenticationContext.Provider value={ authenticationContext }>
                    { children }
                </AuthenticationContext.Provider>
            </PlatoClientContext.Provider>
        );
    }

    return (
        <Loading />
    );
}

export default PlatoClientProvider;

function Loading() {
    return (
        <>
            <NavBar />
            <Container>
                <Row
                    style={ {
                        marginTop: '40px',
                        marginBottom: '40px',
                    } }
                    className="text-center"
                >
                    <Col>
                        <Loader
                            type="TailSpin"
                            color="#fd6413"
                            height={ 100 }
                            width={ 100 }
                        />
                    </Col>
                </Row>
                <Row className="text-center">
                    <Col>
                        <p>Logging you into <strong>PMS</strong></p>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
